/* eslint-disable @typescript-eslint/no-empty-function */
import { useRef } from "react"

import { PropTypes } from "@l2r-front/l2r-proptypes"
import { BottomSheetHeights } from "@l2r-front/l2r-utils"

import * as Styled from "./ThresholdBottomSheet.styled"


const interactiveElements = [
    "puller-container",
    "treshold-bottom-sheet",
]

export function ThresholdBottomSheet(props) {
    const {
        disablePortal,
        height,
        setHeight,
        ...bottomSheetProps
    } = props

    const touchStartY = useRef(null)
    const drawerStartHeight = useRef(null)

    const isScrollable = (el) => {
        const { overflowY } = window.getComputedStyle(el)
        return ["auto", "scroll"].includes(overflowY) && el.scrollHeight > el.clientHeight
    }

    const findTouchedWithinSheet = (el) => {
        if (!el) {
            return { isTouchedWithin: false, scrollableEl: null }
        }

        const scrollableEl = isScrollable(el)
            ? {
                scrollTop: el.scrollTop,
                scrollHeight: el.scrollHeight,
                clientHeight: el.clientHeight,
            }
            : null

        if (interactiveElements.includes(el.id)) {
            return { isTouchedWithin: true, scrollableEl }
        }

        const result = findTouchedWithinSheet(el.parentElement)
        return {
            isTouchedWithin: result.isTouchedWithin,
            scrollableEl: result.scrollableEl || scrollableEl,
        }
    }

    const allowBottomSheetEvent = (event, direction = 0) => {
        const { isTouchedWithin, scrollableEl } = findTouchedWithinSheet(event.target)

        if (!isTouchedWithin) {
            return false
        }

        if (!scrollableEl) {
            return true
        }

        const isAtTop = scrollableEl.scrollTop <= 0
        const isAtBottom =
            scrollableEl.scrollTop + scrollableEl.clientHeight >= scrollableEl.scrollHeight - 1

        return (direction > 0 && isAtTop) || (direction < 0 && isAtBottom)
    }
    const handleTouchStart = (event) => {
        touchStartY.current = event.touches[0].clientY
        drawerStartHeight.current = height
    }

    const handleTouchMove = (event) => {
        if (touchStartY.current !== null && drawerStartHeight.current !== null) {
            const touchY = event.touches[0].clientY
            const deltaY = touchStartY.current - touchY
            const direction = -deltaY
            if (!allowBottomSheetEvent(event, direction)) {
                return
            }

            const screenHeight = window.innerHeight
            let newHeight = drawerStartHeight.current + (deltaY / screenHeight) * 100
            newHeight = Math.max(BottomSheetHeights.CLOSED, Math.min(BottomSheetHeights.FULL, newHeight))

            setHeight(newHeight)
        }
    }

    const handleTouchEnd = (event) => {
        if (touchStartY.current !== null && drawerStartHeight.current !== null) {
            const touchY = event.changedTouches[0].clientY
            const deltaY = touchStartY.current - touchY
            const direction = -deltaY

            if (!allowBottomSheetEvent(event, direction)) {
                return
            }

            const screenHeight = window.innerHeight
            const estimatedHeight = drawerStartHeight.current + (deltaY / screenHeight) * 100

            const availableHeights = Object.entries(BottomSheetHeights)
                .filter((_, height) => height !== BottomSheetHeights.NONE)
            const closestState = availableHeights.reduce(
                (closestKey, [key, value]) => {
                    const currentDiff = Math.abs(estimatedHeight - value)
                    const closestDiff = Math.abs(estimatedHeight - BottomSheetHeights[closestKey])
                    return currentDiff < closestDiff ? key : closestKey
                },
                "CLOSED",
            )
            setHeight(BottomSheetHeights[closestState])
            touchStartY.current = null
            drawerStartHeight.current = null
        }
    }

    return (
        <Styled.BottomSheet
            {...bottomSheetProps}
            id="treshold-bottom-sheet"
            open
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            displayPuller
            height={height}
            hideBackdrop
            onClose={props.onClose ?? (() => { })}
            onOpen={() => { }}
            allowSwipeInChildren={false}
            disableDiscovery
            disableBackdropTransition
            disableSwipeToOpen
            disablePortal={disablePortal}
            disableEnforceFocus
        />
    )
}

ThresholdBottomSheet.propTypes = {
    disablePortal: PropTypes.bool,
    height: PropTypes.number.isRequired,
    setHeight: PropTypes.func.isRequired,
    className: PropTypes.string,
    onClose: PropTypes.func,
    children: PropTypes.node,
}

ThresholdBottomSheet.defaultProps = {
    disablePortal: false,
    className: "",
    onClose: null,
    children: null,
}